import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { Link, useParams } from "react-router-dom";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import RowLoader from "../UI/RowLoader";
import EditIcon from "../Svgs/EditIcon";
import DeleteIcon from "../Svgs/DeleteIcon";
import CustomPagination from "../CustomPagination";
import { deleteLessonInCourseApi, getRecordedLessons } from "../../apis";
import { transformKeys } from "../../_helper";
import Swal from "sweetalert2";
import ViewLessonModal from "./ViewLessonModal";

const RecordedSessions = ({
  title,
  className = "tw-container tw-mx-auto tw-p-5 tw-bg-white tw-rounded-[10px] tw-shadow-md tw-relative",
}) => {
  const { courseId } = useParams();
  const [recordedSessions, setRecordedSessions] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setIsLoading(true);
    getRecordedLessons(courseId, currentPage)
      .then((data) => {
        setPaginator({
          current_page: data.data.pagination.current_page,
          last_page: data.data.pagination.total_pages,
        });
        setRecordedSessions(
          data.data.lessons.map((lesson) => transformKeys(lesson))
        );
        setCurrentPage(1);
      })
      .catch((error) => {
        setIsError(
          error.response?.data?.message ||
            "Error while fetching course recorded lessons"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [courseId, currentPage]);

  // Handle Lesson Deletion
  const handleDeleteLesson = (lessonId) => {
    if (!lessonId) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        deleteLessonInCourseApi(courseId, lessonId)
          .then(() => {
            Swal.fire("Deleted!", "The lesson has been deleted.", "success");
            return getRecordedLessons(courseId, currentPage);
          })
          .then((data) => {
            getRecordedLessons(courseId, currentPage)
              .then((data) => {
                setPaginator({
                  current_page: data.data.pagination.current_page,
                  last_page: data.data.pagination.total_pages,
                });
                setRecordedSessions(
                  data.data.lessons.map((lesson) => transformKeys(lesson))
                );
                setCurrentPage(1);
              })
              .catch((error) => {
                setIsError(
                  error.response?.data?.message ||
                    "Error while fetching course recorded lessons"
                );
              })
              .finally(() => {
                setIsLoading(false);
              });
          })
          .catch((error) => {
            Swal.fire("Error", "Failed to refetch the lessons", "error");
            console.error("Error deleting lesson:", error);
          })
          .finally(() => setIsLoading(false));
      }
    });
  };
  const columns = useMemo(() => {
    return createColumns(
      recordedSessions,
      [
        {
          name: "Edit",
          selector: (row) => row.name,
          ui: (row) => (
            <Link to={`./edit-lesson/${row.id}`} className="tw-flex tw-gap-1">
              <EditIcon width="22px" height="30px" />
            </Link>
          ),
        },
        {
          name: "Delete",
          selector: (row) => row.name,
          ui: (row) => (
            <button
              className="!tw-m-0 !tw-p-0"
              onClick={() => handleDeleteLesson(row.id)}
            >
              <DeleteIcon width="17px" />
            </button>
          ),
        },
      ],
      ["id", "course_id"],
      true
    ).map((col) => {
      if (col.name === "Lesson Link") {
        return {
          ...col,
          selector: (row) => (
            <div className="tw-justify-center tw-flex tw-cursor-pointer">
              <ViewLessonModal videoUrl={row.lesson_link} />
            </div>
          ),
          sortable: false,
          width: "1fr", // أو حسب ما تريد
        };
      }

      return {
        ...col,
        sortable: col.name === "Num",
        width: ["Num", "Edit", "Delete"].includes(col.name) ? "80px" : "1fr",
      };
    });
  }, [recordedSessions]);

  return (
    <section className={className}>
      <div className="tw-pb-2 tw-flex max-md:tw-flex-col max-md:tw-gap-4 justify-between tw-items-center tw-mb-4 tw-border-b">
        <h3 className="tw-text-xl tw-font-semibold tw-text-red-600 tw-m-0">
          {title}
        </h3>
      </div>
      {isError ? (
        <p className="tw-text-red-500 tw-text-center">{isError}</p>
      ) : (
        <DataTable
          columns={columns}
          data={recordedSessions}
          customStyles={DATATABLE_CUSTOM_STYLES}
          progressPending={isLoading}
          progressComponent={<RowLoader />}
        />
      )}
      <div className="flex justify-center mt-4">
        <CustomPagination
          totalPages={paginator?.last_page}
          currentPage={paginator?.current_page || 1}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default RecordedSessions;
